import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Security: Detect and block malicious input
const containsMaliciousContent = (text) => {
  if (!text) return false;
  
  const lowerText = text.toLowerCase();
  
  // Block URLs and links
  const urlPatterns = [
    /https?:\/\//i,
    /www\./i,
    /\.com|\.co\.za|\.net|\.org|\.io|\.xyz|\.ru/i,
    /bit\.ly|tinyurl|goo\.gl/i,
  ];
  
  // Block code injection attempts
  const codePatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /eval\(/i,
    /document\./i,
    /window\./i,
  ];
  
  // Block SQL injection attempts
  const sqlPatterns = [
    /drop\s+table/i,
    /delete\s+from/i,
    /insert\s+into/i,
    /union\s+select/i,
    /;\s*--/i,
  ];
  
  for (const pattern of [...urlPatterns, ...codePatterns, ...sqlPatterns]) {
    if (pattern.test(text)) return true;
  }
  
  return false;
};

// Security: Sanitize input
const sanitizeInput = (text) => {
  if (!text) return '';
  return text
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/&/g, '&amp;')
    .trim()
    .slice(0, 500); // Limit length
};

const SupportChatbot = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize welcome message with translation
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: t('chatbot.welcome')
    }]);
  }, [t]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const userMessage = sanitizeInput(input);
    if (!userMessage) return;

    // Security check
    if (containsMaliciousContent(input)) {
      toast.error("Links and code are not allowed for security reasons.");
      setInput('');
      return;
    }

    // Add user message
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/chatbot/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ 
          message: userMessage,
          conversation_history: newMessages.slice(-6) // Last 6 messages for context
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      setMessages([...newMessages, { 
        role: 'assistant', 
        content: data.response 
      }]);
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages([...newMessages, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again in a moment, or email us at support@careerstep.co.za for help."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Chat Button - positioned higher on mobile to avoid cookie banner overlap */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 sm:bottom-6 right-4 sm:right-6 z-50 w-14 h-14 bg-amber-500 hover:bg-amber-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${isOpen ? 'scale-0' : 'scale-100'}`}
        aria-label="Open support chat"
        data-testid="chatbot-open-btn"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Window */}
      <div 
        className={`fixed bottom-24 sm:bottom-6 right-4 sm:right-6 z-50 w-[360px] max-w-[calc(100vw-32px)] sm:max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col transition-all duration-300 origin-bottom-right ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}`}
        style={{ height: 'min(500px, calc(100vh - 120px))' }}
      >
        {/* Header */}
        <div className="bg-slate-900 text-white px-4 py-3 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">{t('chatbot.title')}</h3>
              <p className="text-xs text-slate-400">{t('chatbot.subtitle')}</p>
            </div>
          </div>
          <button 
            onClick={() => setIsOpen(false)}
            className="p-1 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Close chat"
            data-testid="chatbot-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Security Notice */}
        <div className="bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2 border-b border-amber-100">
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          <span>{t('chatbot.securityNote')}</span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div 
              key={idx} 
              className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                msg.role === 'user' ? 'bg-slate-200' : 'bg-amber-100'
              }`}>
                {msg.role === 'user' ? (
                  <User className="w-4 h-4 text-slate-600" />
                ) : (
                  <Bot className="w-4 h-4 text-amber-600" />
                )}
              </div>
              <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                msg.role === 'user' 
                  ? 'bg-slate-900 text-white rounded-br-sm' 
                  : 'bg-slate-100 text-slate-800 rounded-bl-sm'
              }`}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-2">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <Bot className="w-4 h-4 text-amber-600" />
              </div>
              <div className="bg-slate-100 px-4 py-3 rounded-xl rounded-bl-sm">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-3 border-t border-slate-200">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('chatbot.placeholder')}
              className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              disabled={isLoading}
              maxLength={500}
              data-testid="chatbot-input"
            />
            <Button 
              type="submit" 
              disabled={!input.trim() || isLoading}
              className="bg-amber-500 hover:bg-amber-600 text-white px-3"
              data-testid="chatbot-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default SupportChatbot;
